import { observer } from 'mobx-react';
import React from 'react';

import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

import Chip from 'components/Chip';

import { UserRole, UserRoleNames } from 'core/constants';

import { useStore } from 'utils/hooks/store';

import API from 'utils/api';
import { getErrorMessage } from 'utils/api/errors';
import Logger from 'utils/logger';

import { AxiosError } from 'axios';

import { UpdatedUserPayload } from 'utils/events/types';
import { postMessage } from 'utils/events/broadcast';
import { EventType } from 'utils/events/constants';

import useMappedFields from '../../../hooks/useMappedFields';

import { colors } from 'theme/colors';

import { HelperText } from './styles';

const RolePicker: React.FunctionComponent = () => {
  const { userDetails, notification, session, organisationStructure } = useStore();
  const { scimEnabled } = useMappedFields();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [updating, setUpdating] = React.useState(false);

  const details = userDetails.details;
  if (!details) return null;

  const topUnit = organisationStructure.tree[0]?.uuid;
  const atTopUnit = Boolean(details.homeUnit) && details.homeUnit === topUnit;

  const disabled =
    updating ||
    !details.isActive ||
    scimEnabled ||
    session.user?.uuid === details.uuid ||
    session.user?.role !== UserRole.AccountManager;

  const onRoleUpdate = async (role: UserRole): Promise<void> => {
    setAnchorEl(null);
    setUpdating(true);
    try {
      const response = await API.user.updateUserData(details.uuid, { role })();
      const payload: UpdatedUserPayload = response.data;
      postMessage(EventType.UpdatedUser, payload);
      notification.enqueueSuccessSnackbar('User role updated successfully!');
    } catch (e) {
      Logger.error('Invalid update user details API response', e);
      notification.enqueueErrorSnackbar(getErrorMessage(e as AxiosError, 'Could not update user role'));
    }
    setUpdating(false);
  };

  return (
    <>
      <Chip
        disabled={disabled}
        label={UserRoleNames[details.role] ?? details.role}
        variant="outlined"
        size="medium"
        fontSize={14}
        iconRight={
          anchorEl ? (
            <ExpandLessIcon sx={{ color: 'text.secondary' }} />
          ) : (
            <ExpandMoreIcon sx={{ color: 'text.secondary' }} />
          )
        }
        iconRightDisableInnerMargin
        onClick={(event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget)}
        colors={{
          background: colors.common.white,
          border: colors.action.disabled,
          hoverBackground: colors.divider,
        }}
        enableHover
      />
      <Menu keepMounted anchorEl={anchorEl} onClose={() => setAnchorEl(null)} open={Boolean(anchorEl)}>
        {[UserRole.AccountManager, UserRole.Manager, UserRole.User].map((role) => (
          <MenuItem
            key={role}
            disabled={role === UserRole.AccountManager && !atTopUnit}
            onClick={() => onRoleUpdate(role)}
          >
            {UserRoleNames[role]}
            {role === UserRole.AccountManager && !atTopUnit ? <HelperText>Only at the top unit</HelperText> : null}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default observer(RolePicker);
